"use client";

import { Link } from "@/components/common/Link";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
const fallbackTiles = [{
  title: "International Escapes",
  count: "12 destinations",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/global_travel_landscape.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/global_travel_landscape.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/global_travel_landscape.png-wvc-sizes",
  alt: "Breathtaking mountain landscape in Switzerland"
}, {
  title: "Soloist Expeditions",
  count: "8 adventures",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/solo_trekking_kashmir.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/solo_trekking_kashmir.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/solo_trekking_kashmir.webp-wvc-sizes",
  alt: "Solo traveler exploring the serene valleys of Kashmir"
}, {
  title: "Private Departures",
  count: "Custom curated",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/private_luxury_resort.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/private_luxury_resort.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/private_luxury_resort.png-wvc-sizes",
  alt: "Luxury private villa overlooking the ocean in Maldives"
}];
function GalleryTiles() {
  const {
    terms,
    loading,
    error
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
        {[0, 1, 2, 3].map(i => <div data-index={i} key={i} className="flex flex-col space-y-4">
            <div className="aspect-[3/4] w-full bg-muted animate-pulse rounded-lg shadow-md" />
            <div className="h-6 w-3/4 bg-muted animate-pulse rounded-md" />
            <div className="h-4 w-1/2 bg-muted animate-pulse rounded-md" />
          </div>)}
      </div>;
  }
  const tiles = !error && terms && terms.length > 0 ? terms.slice(0, 4).map((term, i) => ({
    title: term.name,
    count: term.description ? term.description.substring(0, 20) : "Explore Collection",
    image: term.image_url || fallbackTiles[i % fallbackTiles.length].image,
    srcset: term.image_url ? "" : fallbackTiles[i % fallbackTiles.length].srcset,
    sizes: term.image_url ? "" : fallbackTiles[i % fallbackTiles.length].sizes,
    alt: term.name,
    link: term.link || "#"
  })) : fallbackTiles.map(t => ({
    ...t,
    link: "/ShopPage"
  }));
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
      {tiles.map((tile, i) => <motion.div key={i} data-index={i} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: i * 0.1
    }}>
          <Link to={tile.link} className="group flex flex-col h-full bg-card rounded-lg shadow-lg overflow-hidden transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
            <div className="aspect-[3/4] w-full overflow-hidden relative">
              <img src={tile.image} {...tile.srcset ? {
            "data-wvc-srcset": tile.srcset
          } : {}} {...tile.sizes ? {
            "data-wvc-sizes": tile.sizes
          } : {}} alt={tile.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                <span className="text-white text-sm font-sans tracking-wider uppercase flex items-center gap-2">
                  View Details <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
            <div className="p-4 flex flex-col flex-grow">
              <h3 className="font-default font-medium text-foreground text-xl md:text-2xl leading-tight mb-2">
                {tile.title}
              </h3>
              {tile.count && <p className="font-sans text-muted-foreground text-xs tracking-widest uppercase mt-auto">
                  {tile.count}
                </p>}
            </div>
          </Link>
        </motion.div>)}
    </div>;
}
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 4,
  orderby: "name",
  order: "asc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
export default function Journeys() {
  return <section data-section-id="17683" id="the-global-collection" className="bg-background text-foreground py-12 lg:py-18 overflow-hidden isolate">
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12 lg:px-[4.5rem]">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 md:mb-24">
          <div className="max-w-[700px] animate-slide-in-left">
            <span className="inline-block font-sans text-primary text-sm font-semibold tracking-[0.2em] uppercase mb-4">
              The Global Collection
            </span>
            <h2 className="font-default font-light text-foreground leading-[1.1] tracking-tight" style={{
            fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
            letterSpacing: "-0.02em"
          }}>
              Curated escapes, <em className="italic font-light text-secondary">tailored</em> for you.
            </h2>
            <p className="mt-6 font-default font-normal text-muted-foreground leading-relaxed max-w-[540px]" style={{
            fontSize: "clamp(1.125rem, 1.2vw, 1.25rem)"
          }}>
              From global luxury getaways to intimate domestic solo journeys, Innoleaf transforms travel into a seamless art form.
            </p>
          </div>

          <div className="flex shrink-0 animate-fade-in-up">
            <Link to="https://innoleafstays.com/shop/" className="group inline-flex items-center gap-3 font-sans font-semibold text-foreground text-sm tracking-widest uppercase py-4 px-8 border border-border rounded-lg transition-all duration-300 hover:bg-primary hover:text-primary-foreground hover:border-primary">
              Explore all journeys
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -right-24 -top-24 w-96 h-96 bg-primary/5 rounded-lg blur-3xl -z-10" />
          
          <EcommerceTermsProvider wp_term_query={{
          taxonomy: "product_cat",
          per_page: 4,
          orderby: "name",
          order: "asc"
        }}>
            <GalleryTiles />
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>;
}